import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
// datetime-local gives "YYYY-MM-DDTHH:MM" in browser-local time. Around DST
// boundaries this is ambiguous — March 8 02:30 doesn't exist; Nov 1 01:30
// happens twice. We accept the browser's `new Date(local)` interpretation
// (Chrome and Safari both pick the post-DST instant) and document that
// edge in the UI. Cap year ≥ 2024 to filter epoch-confused inputs.
function localInputToIso(v) {
    if (!v)
        return undefined;
    const d = new Date(v);
    if (isNaN(d.getTime()))
        return undefined;
    if (d.getUTCFullYear() < 2024)
        return undefined;
    return d.toISOString();
}
export default function SearchBox({ onSearch, busy, availableCameras = [] }) {
    const [q, setQ] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [includeEmpty, setIncludeEmpty] = useState(false);
    const [rangeError, setRangeError] = useState(null);
    // Set semantics: easy to add/remove via checkbox. Converted to a
    // sorted number[] at submit time so the URL/POST body is stable.
    const [selectedCams, setSelectedCams] = useState(new Set());
    // Reset selection on customer switch. `availableCameras` is recomputed
    // by useEventsController whenever `selectedCustomer` changes, so we
    // detect the switch by comparing the camera signature (sorted bIDs).
    // Without this, a checkbox checked for cs256 cam1 ("Front Door")
    // silently migrates to cs2427 cam1 ("Loading Dock") after the
    // operator picks a different customer — same bID, different camera,
    // wrong filter.
    const availableSig = availableCameras.map(c => c.bID).sort((a, b) => a - b).join(",");
    const prevSig = useRef(null);
    useEffect(() => {
        if (prevSig.current !== null && prevSig.current !== availableSig) {
            setSelectedCams(new Set());
        }
        prevSig.current = availableSig;
    }, [availableSig]);
    const submit = (e) => {
        e.preventDefault();
        setRangeError(null);
        const since = localInputToIso(from);
        const until = localInputToIso(to);
        if (since && until && since >= until) {
            setRangeError("From must be earlier than To.");
            return;
        }
        const cams = Array.from(selectedCams).sort((a, b) => a - b);
        const range = {
            q: q.trim(), since, until, includeEmpty,
            bCameras: cams.length > 0 ? cams : undefined,
        };
        if (range.q.length === 0 && !range.since && !range.until && cams.length === 0)
            return;
        onSearch(range);
    };
    const clear = () => {
        setQ("");
        setFrom("");
        setTo("");
        setRangeError(null);
        setSelectedCams(new Set());
        onSearch({ q: "", includeEmpty });
    };
    const toggleCam = (bID) => {
        setSelectedCams(prev => {
            const next = new Set(prev);
            if (next.has(bID))
                next.delete(bID);
            else
                next.add(bID);
            return next;
        });
    };
    const hasAny = q.length > 0 || from.length > 0 || to.length > 0 || selectedCams.size > 0;
    return (_jsxs("form", { className: "search-box", onSubmit: submit, children: [_jsx("input", { type: "text", placeholder: "search descriptions ('person in red jacket', 'someone fell')", value: q, onChange: (e) => setQ(e.target.value), disabled: busy, "aria-label": "search query" }), _jsxs("span", { className: "when", children: [_jsx("label", { htmlFor: "search-from", children: "From" }), _jsx("input", { id: "search-from", type: "datetime-local", value: from, onChange: (e) => setFrom(e.target.value), disabled: busy })] }), _jsxs("span", { className: "when", children: [_jsx("label", { htmlFor: "search-to", children: "To" }), _jsx("input", { id: "search-to", type: "datetime-local", value: to, onChange: (e) => setTo(e.target.value), disabled: busy })] }), _jsx("button", { type: "submit", disabled: busy || !hasAny, className: "primary", children: busy ? "Searching..." : "Search" }), hasAny && (_jsx("button", { type: "button", className: "ghost", onClick: clear, children: "clear" })), _jsxs("label", { className: "include-empty", title: "Include 'no people / no events' descriptions", children: [_jsx("input", { type: "checkbox", checked: includeEmpty, onChange: (e) => {
                            const v = e.target.checked;
                            setIncludeEmpty(v);
                            // Auto-refresh the recent-events list when toggled at rest
                            if (!q && !from && !to && selectedCams.size === 0)
                                onSearch({ q: "", includeEmpty: v });
                        }, disabled: busy }), _jsx("span", { children: "show empty" })] }), availableCameras.length > 1 && (_jsxs("fieldset", { className: "camera-filter", "aria-label": "filter by camera", children: [_jsx("legend", { children: "cameras" }), availableCameras.map(cam => (_jsxs("label", { className: "camera-option", children: [_jsx("input", { type: "checkbox", checked: selectedCams.has(cam.bID), onChange: () => toggleCam(cam.bID), disabled: busy }), _jsx("span", { children: cam.name ? cam.name : `cam ${cam.bID}` })] }, cam.bID)))] })), rangeError && _jsx("span", { className: "search-range-error", children: rangeError })] }));
}
